import React from 'react';
import Cropper from 'react-cropper';
import Compressor from 'compressorjs';

import { useToast } from 'hooks';
import { FirebaseImage } from 'types';

import { Button } from '../buttons';
import { CommonDialog } from '../dialog';

interface Props {
  uri?: string;
  open: boolean;
  onClose: () => void;
  onCrop: (data: FirebaseImage) => void;
}

export const CropperDialog = ({ open, onCrop, uri, onClose }: Props) => {
  const { handleError } = useToast();

  const cropperRef = React.useRef<HTMLImageElement>(null);

  const onCropSuccess = (thumbnail: File | Blob, croppedFile: File) => {
    let thumbnailResult: File;

    if (thumbnail instanceof Blob) {
      thumbnailResult = new File([thumbnail as Blob], 'thumbnail.jpeg', {
        type: 'image/jpeg',
      });
    } else {
      thumbnailResult = thumbnail as File;
    }

    onCrop({ thumbnailFile: thumbnailResult, file: croppedFile });
  };

  const handleCrop = async () => {
    const imageElement: any = cropperRef?.current;
    const cropperElement: any = imageElement?.cropper;

    const dataUrl = cropperElement.getCroppedCanvas().toDataURL();

    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const croppedFile = new File([blob], 'cropped-image.jpeg', {
      type: 'image/jpeg',
    });

    new Compressor(croppedFile, {
      quality: 0.6,
      error: (err) => handleError(err),
      success: (compressedFile) => onCropSuccess(compressedFile, croppedFile),
    });
  };

  const ButtonComponent = React.useCallback(() => {
    return (
      <Button
        title="Crop"
        type="button"
        variant="default"
        palette="primary"
        onClick={handleCrop}
      />
    );
  }, [handleCrop]);

  return (
    <CommonDialog
      title="Cropper"
      open={open}
      onClose={onClose}
      ButtonComponent={ButtonComponent}
    >
      <Cropper
        center
        movable
        src={uri}
        ref={cropperRef}
        autoCropArea={1}
        aspectRatio={1 / 1}
        style={{ maxWidth: '100%', maxHeight: '450px' }}
      />
    </CommonDialog>
  );
};
